import { LoadingOutlined } from "@ant-design/icons";
import { Button, Col, Layout, Row, Space, Spin, Tabs, Typography } from "antd";
import TabPane from "antd/es/tabs/TabPane";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ActiveCollectionIcon from "../../../assets/icons/active-collection.svg";
import ActiveGridIcon from "../../../assets/icons/active-grid.svg";
import BookmarkOtherProfileIcon from "../../../assets/icons/bookmark-other-profile.svg";
import ChatIcon from "../../../assets/icons/chat-icon.svg";
import CollectionIcon from "../../../assets/icons/collection.svg";
import GridIcon from "../../../assets/icons/grid.svg";
import VideoIcon from "../../../assets/icons/jouer.svg";
import LocationIcon from "../../../assets/icons/location.svg";
import LockIcon from "../../../assets/icons/lock.svg";
import ShareOtherProfileIcon from "../../../assets/icons/share-other-profile.svg";
import VerifyIcon from "../../../assets/icons/verify.svg";
import AvatarComponent from "../../../components/AvatarComponent";
import CollectionPublicGridComponent from "../../../components/Collections/CollectionGridComponent/CollectionPublicGridComponent";
import FreeChatButtonComponent from "../../../components/FreeChatButtonComponent/FreeChatButtonComponent";
import PublicAuthentificationModalComponent from "../../../components/PublicAuthentificationModalComponent";
import SpinnerComponent from "../../../components/SpinnerComponent/SpinnerComponent";
import ThreeDotsComponent from "../../../components/ThreeDotsComponent";
import { REQUEST_LOADING_STATES, THUMBNAIL_TYPES } from "../../../helpers/constant";
import { getNumberLengthClass } from "../../../helpers/numberClassHelper";
import useFetchPublicFeeds from "../../../hooks/feedHooks/useFetchPublicFeeds";
import useProfileCustomPageInfos from "../../../hooks/global/useProfileCustomPageInfos";
import useFetchPublicUserInfos from "../../../hooks/userHooks/useFetchPublicUserInfos";
import { generateImageSeoAttributes } from "../../../utls/imageAltGenerator";
import "./index.less";
import PublicProfileEvent from "./PublicProfileEvent";
import HiddenSeoContent from "../../../components/HiddenSeoContent/HiddenSeoContent";
import { getProfileSeoContent, getProfileFaqJsonLd } from "../../../data/profile-seo-content";
import { Helmet } from "react-helmet-async";

const MAX_NAME_LENGTH = 16;

const PublicProfilePageV2: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { username } = useParams();
  const { loading, error, publicUser } = useFetchPublicUserInfos(username);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const iconSize = publicUser && publicUser?.name && publicUser?.name.length > MAX_NAME_LENGTH ? "16px" : "22px";
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const { publicFeeds, feedsLoading } = useFetchPublicFeeds(username, activeTab);

  useProfileCustomPageInfos({
    username: username || "TWIZZ.COM",
    name: publicUser?.name,
    avatarPath: publicUser?.avatar,
    lastModificationDate: publicUser?.lastModificationDate,
    publicationsCount: publicUser?.stats?.totalCollections || 0,
    t,
  });

  useEffect(() => {
    if (publicUser && activeTab === null) {
      const savedTab = localStorage.getItem("activeProfileTab");
      setActiveTab(savedTab || publicUser?.defaultPage || "feeds");
    }
  }, [publicUser, activeTab]);

  // Redirect to home if user not found (404 error)
  useEffect(() => {
    if (error && loading === REQUEST_LOADING_STATES.FAILED) {
      navigate("/");
    }
  }, [error, loading, navigate]);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const hideModal = () => {
    setIsModalVisible(false);
  };

  const handleTabChange = (key: string) => {
    localStorage.setItem("activeProfileTab", key);
    setActiveTab(key);
  };

  const faqJsonLd = username ? getProfileFaqJsonLd(username) : null;

  return (
    <Layout className={`gradient-background-profile no-scroll-horizontal ${isModalVisible ? "no-scroll-page" : ""}`}>
      {faqJsonLd && (
        <Helmet>
          <script type="application/ld+json">{faqJsonLd}</script>
        </Helmet>
      )}
      <Layout.Content className="content fanProfileContentWrapper background-transparent">
        <>
          {publicUser?.name && (
            <>
              <h1 className="seo-hidden">{publicUser.username} leak</h1>
              <h2 className="seo-hidden">Les leaks et contenus exclusifs de {publicUser.name}</h2>
            </>
          )}
          <Row className="header-container">
            <img src={ShareOtherProfileIcon} width={18} alt="share" onClick={showModal} />
            <div className="header-text-other-profile" aria-hidden="true">
              @{publicUser?.username}
            </div>

            <img src={BookmarkOtherProfileIcon} className="mr-10" width={16} onClick={showModal} alt="follow" />
          </Row>
          <div className="fanProfileTopWrapper">
            <Row align="middle">
              <Col className="colAvatar mr-10" onClick={showModal}>
                <AvatarComponent
                  image={publicUser?.avatar || ""}
                  size={105}
                  fontSize={16}
                  fontWeight={true}
                  profileName={publicUser?.name}
                  usernameAltText={publicUser?.username}
                />
              </Col>
              <Col className="colDetails">
                <Row className="nameRow" align="middle">
                  <div className={`nameTitleOtherProfile`}>{error !== "user-not-found" ? <>{publicUser?.name}</> : t(error as string)}</div>
                  {publicUser?.verifiedAccount && <img className="verifyIcon" src={VerifyIcon} width={iconSize} alt="verify" />}
                </Row>

                <Row className="buttonRow mt-5" align="middle">
                  <div className="profile-button-container">
                    <div className="profile-action-buttons">
                      {publicUser?.chat === "free" ? (
                        <>
                          <FreeChatButtonComponent onClick={showModal} />
                          <Button className="subscribeBtn freeChat" onClick={showModal}>
                            <span>{t("subscribe")}</span>
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button className="subscribeBtn" onClick={showModal}>
                            <span>{t("Subscribe-without-obligation")}</span>
                          </Button>
                          <img src={ChatIcon} onClick={showModal} width={43} alt="Chat" />
                        </>
                      )}
                    </div>
                  </div>
                </Row>
              </Col>
            </Row>
          </div>
          <div className="labelWrapper">
            {publicUser?.city && (
              <Row className="m-5">
                <Col className="colValues location">
                  <img className="locationImg mr-5" src={LocationIcon} width={16} alt="location" />
                  <Typography.Text className="fanProfileValue">{publicUser.city}</Typography.Text>
                </Col>
              </Row>
            )}
            <Row className="mt-10">
              <div
                className="fanProfileDescriptionLabel"
                dangerouslySetInnerHTML={{
                  __html: (publicUser?.description || "").replace(/\n/g, "<br />"),
                }}
              />
            </Row>

            <Row className="mt-10 max-w-80 justify-content-between" align="middle">
              <div className="leftItems">
                <Space>
                  <Col className="colValues">
                    <Typography.Text className={`fanProfileAttributeValue ${getNumberLengthClass(publicUser?.stats?.totalFeeds || 0)}`}>
                      {publicUser?.stats?.totalFeeds || 0}
                    </Typography.Text>
                    <Typography.Text className="fanProfileValue">{t("publications")}</Typography.Text>
                  </Col>
                </Space>

                <Space>
                  <Col className="colValues">
                    <Typography.Text className={`fanProfileAttributeValue ${getNumberLengthClass(publicUser?.stats?.totalCollections || 0)}`}>
                      {publicUser?.stats?.totalCollections || 0}
                    </Typography.Text>
                    <Typography.Text className="fanProfileValue">{t("collections")}</Typography.Text>
                  </Col>
                </Space>
              </div>

              <ThreeDotsComponent onClick={showModal} />
            </Row>
          </div>

          {publicUser && <PublicProfileEvent creatorId={publicUser._id!} onTakeTicketClick={showModal} />}
        </>
        <Col className="feeds-container">
          {!activeTab ? (
            <SpinnerComponent />
          ) : (
            <Tabs activeKey={activeTab} onChange={handleTabChange}>
              <TabPane tab={<img src={activeTab === "feeds" ? ActiveGridIcon : GridIcon} width={20} alt="publications icon" />} key="feeds">
                <Row className="mt-10 pr-4 pl-4" gutter={2}>
                  {feedsLoading === REQUEST_LOADING_STATES.PENDING ? (
                    <div className="w-full divSpin">
                      <Spin size="large" indicator={<LoadingOutlined className="spin" spin />} />
                    </div>
                  ) : (
                    publicFeeds?.map((feed, index) => {
                      if (feed.files) {
                        const lock = feed.isPrivate;
                        const isVideo = feed.type === "video";
                        const feedThumbnail = isVideo
                          ? feed.isPrivateForCurrentUser
                            ? feed.files[0].thumbnails?.find((thumb) => thumb.type === THUMBNAIL_TYPES.BLURRED)?.absolutePath
                            : feed.files[0].thumbnails?.find((thumb) => thumb.type === THUMBNAIL_TYPES.CLEAR)?.absolutePath
                          : feed.isPrivateForCurrentUser
                            ? feed.files[0].thumbnails?.find((thumb) => thumb.type === THUMBNAIL_TYPES.BLURRED)?.absolutePath
                            : feed.files[0].thumbnails?.findIndex((thumb) => thumb.type === THUMBNAIL_TYPES.CLEAR) !== -1
                              ? feed.files[0].thumbnails?.find((thumb) => thumb.type === THUMBNAIL_TYPES.CLEAR)?.absolutePath
                              : feed.files[0].url;

                        const imageSeo = generateImageSeoAttributes(publicUser?.username, publicUser?.name, `feed_${index}`);

                        return (
                          <Col className="fanProfilePostWrapper" key={index} span={8} onClick={showModal}>
                            <img className={`fanProfilePost fanProfilePrivatePost`} src={feedThumbnail} alt={imageSeo.alt} title={imageSeo.title} />
                            {lock && <img className="fanProfilePostLockIcon" src={LockIcon} alt="lock" />}
                            {isVideo && <img src={VideoIcon} style={!lock ? { right: "11px" } : {}} alt="video icon" className="feedVideoIcon" />}
                          </Col>
                        );
                      }
                      return null;
                    })
                  )}
                </Row>
              </TabPane>

              <TabPane
                tab={<img src={activeTab === "collection" ? ActiveCollectionIcon : CollectionIcon} width={20} alt="collections icon" />}
                key="collection"
              >
                {publicUser && (
                  <CollectionPublicGridComponent
                    creatorId={publicUser._id!}
                    showModal={showModal}
                    goToPublicationTab={() => handleTabChange("feeds")}
                    profileName={publicUser.name}
                    username={publicUser.username}
                  />
                )}
              </TabPane>
            </Tabs>
          )}
        </Col>
      </Layout.Content>
      <PublicAuthentificationModalComponent avatar={publicUser?.avatar} name={publicUser?.name} visible={isModalVisible} onClose={hideModal} />

      {/* SEO: Links for crawlers */}
      <details>
        <summary style={{ listStyle: "none" }}></summary>
        <a href="/">home</a>
      </details>
      <details>
        <summary style={{ listStyle: "none" }}></summary>
        <a href="/profiles">Creator Profiles</a>
      </details>

      {/* SEO: Custom profile content */}
      {username && getProfileSeoContent(username) && <HiddenSeoContent>{getProfileSeoContent(username)}</HiddenSeoContent>}
    </Layout>
  );
};

export default PublicProfilePageV2;
